/**
 * PATH: packages/job-hub/src/jobs/qa/qaInspectionApi.ts
 * FILE_ID: JOBHUB-QA-INSPECTION-API-001
 * PURPOSE: Inspection, area, checklist, reading, and evidence endpoint client.
 */
import type { JobHubHttpClient } from '../jobHubDataAdapter'
import { normalizeList, qdrivePickerFilesFromResponse } from './qaApiHelpers'
import { postQaInBackground } from './qaBackgroundWrite'
import { withQaDisplayNumbers } from './qaDisplayNumbers'
import { QA_INSPECTIONS_ENDPOINT, qaInspectionsJobQuery } from './qaCrossDoorLinks'
import { createQaInspectionExtras } from './qaInspectionApiExtras'
import type {
  QaApi,
  QaArea,
  QaAreaExecution,
  QaAuditEvent,
  QaBulkAssignResult,
  QaChecklistInstance,
  QaEvidence,
  QaFloor,
  QaInspectionSummary,
  QaItemChangeRequest,
  QaItemInstance,
  QaJobFile,
  QaMoveResult,
  QaPasteResult,
} from './qaTypes'

// Edge proxy_read_timeout is 120s; the 30s client default abandoned writes the server still finished.
const QA_SLOW_TIMEOUT_MS = 120_000

type InspectionApi = Omit<
  QaApi,
  'listQuoteSheets' | 'previewQaPlan' | 'generateFromQuoteSheet' | 'generateFromQuoteSheets' |
  'retryQuoteIngest' |
  'listReports' | 'listItpLibrary' | 'createItpLibraryEntry' | 'getItpLibraryEntry' | 'generateReport' | 'renderItpPdf' | 'listItpFromMailboxes' | 'sendItpEmail' | 'itpOnedriveStatus' | 'downloadReport' | 'downloadReportFile' | 'reportDownloadUrl' |
  'listTemplates' | 'getTemplate' | 'generateFromTemplates' | 'insertTemplate' | 'insertBlank' | 'searchProducts' |
  'createTemplate' | 'publishTemplate' | 'restoreDefaults' |
  'setSignoffRequired' | 'requestHoldPoint' | 'approveHoldPoint' | 'rejectHoldPoint'
>

export function createQaInspectionApi(
  client: JobHubHttpClient,
  headers: Record<string, string> | undefined,
): InspectionApi {
  return {
    ...createQaInspectionExtras(client, headers),
    async listInspections(jobId) {
      const response = await client.get(QA_INSPECTIONS_ENDPOINT, {
        params: qaInspectionsJobQuery(jobId),
        headers,
      })
      return normalizeList<QaInspectionSummary>(response.data)
    },
    async ensureGeneral(jobId) {
      // Reused from: apps/qa/src/pages/records-list/openJobQaInHub.ts (openJobQaInHub)
      await client.post('/api/qa/inspections/ensure-general/', { job_id: jobId }, { headers })
    },
    async renameInspection(inspectionId, name) {
      const response = await client.patch(
        `/api/qa/inspections/${inspectionId}/`,
        { inspection_name: name },
        { headers },
      )
      return response.data as QaInspectionSummary
    },
    async duplicateInspection(inspectionId, body) {
      const response = await postQaInBackground<QaInspectionSummary>(
        client,
        `/api/qa/inspections/${inspectionId}/duplicate/`,
        body ?? {},
        headers,
      )
      return response.data
    },
    async duplicateInspectionMany(inspectionId, count) {
      const response = await postQaInBackground<{ group: { id: string; name: string }; inspections: QaInspectionSummary[] }>(
        client,
        `/api/qa/inspections/${inspectionId}/duplicate-many/`,
        { count },
        headers,
      )
      return response.data
    },
    async pasteQaNodes(body) {
      // Reused from: packages/job-hub/src/jobs/qa/qaInspectionApi.ts (duplicateInspection)
      const response = await client.post('/api/qa/paste/', body, { headers, timeout: QA_SLOW_TIMEOUT_MS })
      return response.data as QaPasteResult
    },
    async assignInspectionGroup(body) {
      // Reused from: packages/job-hub/src/jobs/qa/qaInspectionApi.ts (renameInspection client and headers)
      const response = await client.post('/api/qa/inspections/group/', body, { headers })
      return response.data as QaInspectionSummary[]
    },
    async renameArea(areaId, name) {
      const response = await client.patch(`/api/qa/areas/${areaId}/`, { area_name: name }, { headers })
      return response.data as QaArea
    },
    async setAreaProductNames(areaId, productNames) {
      const response = await client.patch(
        `/api/qa/areas/${areaId}/`,
        { product_names: productNames },
        { headers },
      )
      return response.data as QaArea
    },
    async setChecklistProductNames(checklistId, productNames) {
      const response = await client.patch(
        `/api/qa/checklist-instances/${checklistId}/`,
        { product_names: productNames },
        { headers },
      )
      return response.data as QaChecklistInstance
    },
    async setAreaWorkStatus(areaId, workStatus) {
      const response = await client.post(
        `/api/qa/areas/${areaId}/set-work-status/`,
        { work_status: workStatus },
        { headers },
      )
      return response.data as QaArea
    },
    async assignArea(areaId, userId) {
      const response = await client.post(
        `/api/qa/areas/${areaId}/assign/`,
        { user_id: userId },
        { headers },
      )
      return response.data as QaArea
    },
    async assignItem(itemId, userId) {
      const response = await client.post(
        `/api/qa/item-instances/${itemId}/assign/`,
        { user_id: userId },
        { headers },
      )
      return response.data as QaItemInstance
    },
    async bulkAssign(inspectionId, body) {
      const response = await client.post(
        `/api/qa/inspections/${inspectionId}/bulk-assign/`,
        body,
        { headers },
      )
      return response.data as QaBulkAssignResult
    },
    async deleteInspection(inspectionId) {
      await client.delete(`/api/qa/inspections/${inspectionId}/`, { headers, timeout: QA_SLOW_TIMEOUT_MS })
    },
    async listAreas(inspectionId) {
      const response = await client.get('/api/qa/areas/', {
        params: { inspection_id: inspectionId },
        headers,
      })
      return normalizeList<QaArea>(response.data)
    },
    async listFloors(inspectionId) {
      const response = await client.get('/api/qa/floors/', {
        params: { inspection_id: inspectionId },
        headers,
      })
      return normalizeList<QaFloor>(response.data)
    },
    async moveTreeNodes(nodeType, nodeIds, destination) {
      const response = await client.post(
        '/api/qa/tree/move/',
        {
          node_type: nodeType,
          node_ids: nodeIds,
          ...('parentId' in destination
            ? { new_parent_id: destination.parentId }
            : { new_level_name: destination.newLevelName }),
        },
        { headers },
      )
      return response.data as QaMoveResult
    },
    async reorderTreeNodes(nodeType, order) {
      const response = await client.post(
        '/api/qa/tree/reorder/',
        { node_type: nodeType, order },
        { headers },
      )
      return response.data as { reordered: number }
    },
    async getAreaExecution(areaId, options) {
      const response = await client.get(`/api/qa/areas/${areaId}/execution/`, {
        headers,
        params: options?.photos === false ? { photos: 0 } : undefined,
      })
      return withQaDisplayNumbers(response.data as QaAreaExecution)
    },
    async listAreaExecutions(inspectionId, options) {
      const response = await client.get('/api/qa/areas/executions/', {
        headers,
        params: {
          inspection_id: inspectionId,
          ...(options?.photos === false ? { photos: 0 } : {}),
        },
      })
      return normalizeList<QaAreaExecution>(response.data).map((area) => withQaDisplayNumbers(area))
    },
    async getEvidenceUrls(areaId) {
      const response = await client.get(`/api/qa/areas/${areaId}/evidence-urls/`, { headers })
      return response.data as Record<string, { asset_url: string | null; display_url: string | null; photo_url: string | null }>
    },
    async setItemStatus(itemId, status, overrideReason) {
      const body: Record<string, unknown> = { status }
      if (overrideReason) body.override_reason = overrideReason
      const response = await client.post(`/api/qa/item-instances/${itemId}/set_status/`, body, { headers })
      return response.data as QaItemInstance
    },
    async verifyItemCompletion(itemId, verified) {
      const response = await client.post(
        `/api/qa/item-instances/${itemId}/verify-completion/`,
        { verified },
        { headers },
      )
      return response.data as QaItemInstance
    },
    async verifyAreaCompletion(areaId, verified) {
      const response = await client.post(
        `/api/qa/areas/${areaId}/verify-completion/`,
        { verified },
        { headers },
      )
      return response.data as QaArea
    },
    async addChecklist(areaId, title, seedDefaultItems = true) {
      const response = await client.post(
        `/api/qa/areas/${areaId}/add-checklist/`,
        seedDefaultItems
          ? { title: title || 'New checklist' }
          : { title: title || 'New checklist', seed_default_items: false },
        { headers },
      )
      return response.data as QaChecklistInstance
    },
    async renameChecklist(checklistId, title) {
      const response = await client.post(
        `/api/qa/checklist-instances/${checklistId}/rename/`,
        { title },
        { headers },
      )
      return response.data as QaChecklistInstance
    },
    async renameChecklistSection(sectionId, title) {
      const response = await client.patch(
        `/api/qa/template-sections/${sectionId}/`,
        { title },
        { headers },
      )
      return response.data
    },
    async setHoldPoint(itemInstanceId, value) {
      const response = await client.post(
        `/api/qa/item-instances/${itemInstanceId}/set-hold-point/`,
        { has_hold_point: value },
        { headers },
      )
      return response.data as QaItemInstance
    },
    async addItem(areaId, fields, parentItemId, insertAfterNumber, checklistId, origin) {
      const body: Record<string, unknown> = { ...(fields ?? {}) }
      if (parentItemId) body.parent_item = parentItemId
      if (insertAfterNumber) body.insert_after_number = insertAfterNumber
      if (checklistId) body.checklist_instance = checklistId
      if (origin) body.origin = origin
      const response = await client.post(`/api/qa/areas/${areaId}/add-item/`, body, { headers })
      return response.data as QaItemInstance
    },
    async setItemContent(itemId, fields) {
      const response = await client.post(
        `/api/qa/item-instances/${itemId}/set-content/`,
        fields,
        { headers },
      )
      return response.data as QaItemInstance
    },
    async unlockArea(areaId, reason) {
      const response = await client.post(
        `/api/qa/areas/${areaId}/unlock/`,
        { reason },
        { headers },
      )
      return response.data as QaArea
    },
    async updateItem(templateItemId, fields) {
      const response = await client.patch(`/api/qa/template-items/${templateItemId}/`, fields, { headers })
      return response.data
    },
    async deleteItem(itemInstanceId) {
      const response = await client.delete(`/api/qa/item-instances/${itemInstanceId}/`, { headers })
      return response.data as QaItemChangeRequest | undefined
    },
    async deleteChecklist(checklistId) {
      await client.delete(`/api/qa/checklist-instances/${checklistId}/`, { headers })
    },
    async deleteArea(areaId) {
      const response = await client.delete(`/api/qa/areas/${areaId}/`, { headers })
      return response.data
    },
    async duplicateArea(areaId) {
      const response = await client.post(`/api/qa/areas/${areaId}/duplicate/`, {}, { headers })
      return response.data as { id: string }
    },
    async duplicateChecklist(checklistId) {
      const response = await client.post(`/api/qa/checklist-instances/${checklistId}/duplicate/`, {}, { headers })
      return response.data as { id: string }
    },
    async listQaLibrary(jobId) {
      const response = await client.get('/api/report/jobfiles/', {
        params: { job_id: jobId, include: 'qdrive' },
        headers,
      })
      const rows = Array.isArray(response.data) ? response.data : (response.data?.results ?? [])
      return rows.flatMap((row: { asset_id?: string; filename?: string; content_type?: string; file_size?: number | null }) => (
        row.asset_id ? [{
          id: row.asset_id,
          filename: row.filename || '',
          content_type: row.content_type,
          file_size: row.file_size,
        }] : []
      ))
    },
    async raiseItemChange(itemInstanceId, kind, fields) {
      const response = await client.post(
        '/api/qa/item-change-requests/',
        { item_instance: itemInstanceId, kind, ...(fields ?? {}) },
        { headers },
      )
      return response.data as QaItemChangeRequest
    },
    async approveItemChange(changeId, note) {
      const response = await client.post(
        `/api/qa/item-change-requests/${changeId}/approve/`,
        note ? { note } : {},
        { headers },
      )
      return response.data as QaItemChangeRequest
    },
    async rejectItemChange(changeId, note) {
      const response = await client.post(
        `/api/qa/item-change-requests/${changeId}/reject/`,
        note ? { note } : {},
        { headers },
      )
      return response.data as QaItemChangeRequest
    },
    async reorderItems(order) {
      await client.post('/api/qa/item-instances/reorder/', { order }, { headers })
    },
    async clearInspectionStructure(inspectionId) {
      const response = await postQaInBackground<{ cleared_areas: number }>(
        client,
        `/api/qa/inspections/${inspectionId}/clear-structure/`,
        {},
        headers,
      )
      return response.data
    },
    async addTable(inspectionId, name, seedDefaultItems = true) {
      const response = await client.post(
        '/api/qa/areas/add-table/',
        {
          inspection_id: inspectionId,
          ...(name ? { name } : {}),
          seed_default_items: seedDefaultItems,
        },
        { headers },
      )
      return response.data as QaArea
    },
    async aiGenerateItems(areaId, products) {
      const response = await client.post(
        `/api/qa/areas/${areaId}/ai-generate-items/`,
        { products: products ?? [] },
        { headers },
      )
      return response.data as { created: number; items: QaItemInstance[] }
    },
    async setLineChecks(itemId, kind, indexes) {
      const response = await client.post(
        `/api/qa/item-instances/${itemId}/set-line-checks/`,
        { kind, indexes },
        { headers },
      )
      return response.data as QaItemInstance
    },
    async setReading(itemId, capturedValue, capturedUnit) {
      const response = await client.post(
        `/api/qa/item-instances/${itemId}/set-reading/`,
        { captured_value: capturedValue, captured_unit: capturedUnit ?? '' },
        { headers },
      )
      return response.data as QaItemInstance
    },
    async uploadFile(file, jobId) {
      const form = new FormData()
      form.append('file', file)
      form.append('job_id', String(jobId))
      const response = await client.post('/api/qdrive/files/upload/', form, { headers, timeout: QA_SLOW_TIMEOUT_MS })
      return response.data as { id: string }
    },
    async listJobFiles(jobId) {
      // Images only: this list exists to attach a photo already filed on the
      // job, so PDFs and spreadsheets would only be noise in the picker.
      const response = await client.get('/api/qdrive/files/', {
        params: { job_id: jobId },
        headers,
      })
      return normalizeList<QaJobFile>(response.data).filter((file) =>
        (file.content_type ?? '').startsWith('image/'),
      )
    },
    async listQDriveFiles(query) {
      const q = query?.trim()
      const response = q
        ? await client.get('/api/qdrive/search/', { params: { q }, headers })
        : await client.get('/api/qdrive/files/', {
            params: { page_size: 50, ordering: '-uploaded_at' },
            headers,
          })
      return qdrivePickerFilesFromResponse(response.data)
    },
    async addPhotoEvidence({ areaId, itemInstanceId, assetId, description }) {
      const response = await client.post(
        '/api/qa/evidence/',
        {
          area: areaId,
          item_instance: itemInstanceId,
          type: 'photo',
          asset_id: assetId,
          description: description ?? '',
        },
        { headers },
      )
      return response.data as QaEvidence
    },
    async addDocumentEvidence({ areaId, itemInstanceId, assetId, description }) {
      const response = await client.post(
        '/api/qa/evidence/',
        {
          area: areaId,
          item_instance: itemInstanceId,
          type: 'document',
          asset_id: assetId,
          description: description ?? '',
        },
        { headers },
      )
      return response.data as QaEvidence
    },
    async addNoteEvidence({ areaId, itemInstanceId, noteText }) {
      const response = await client.post(
        '/api/qa/evidence/',
        {
          area: areaId,
          item_instance: itemInstanceId,
          type: 'note',
          note_text: noteText,
          folder: 'Field Notes',
        },
        { headers },
      )
      return response.data as QaEvidence
    },
    async deleteEvidence(evidenceId) {
      await client.delete(`/api/qa/evidence/${evidenceId}/`, { headers })
    },
    async listItemAuditEvents(itemId) {
      const response = await client.get('/api/qa/audit-events/', {
        params: { entity_type: 'item_instance', entity_id: itemId },
        headers,
      })
      return normalizeList<QaAuditEvent>(response.data)
    },
  }
}
